import { useCallback, useEffect, useState } from 'react';

import { IconsEnum } from '../../../../../../../assets/icons/types';
import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';
import { StaticImageHelper } from '../../../../../../../helpers/StaticImageHelper';
import { CheckPermissions } from '../../../../../../../helpers/CheckPermissions';

import { modelViewersKeys, words } from '../../../../../../../locales/keys';
import { useTranslate } from '../../../../../../../common/hooks/useTranslate';
import { downloadFile } from '../../../../../../../utils/downloadFile';
import { LifestyleGenerationMode } from '../../../../../../lifestyleGenerations/lifestyleGenerationsTypes';
import { ModelViewerPermissions } from '../../../../../modelViewerConstants';
import { buildDownloadName } from '../../utils/buildDownloadName';
import { ModelViewerImageEditPromptCard } from '../ModelViewerImageEditPromptCard';
import { LifestyleGenerationItem } from '../../hooks/useModelViewerLifestyleGeneration';
import { useReferenceFilesUpload } from '../../hooks/useModelViewerLifestyleGeneration/useReferenceFilesUpload';

interface ModelViewerLifestyleImageOverlayProps {
  url: string;
  modelName?: string;
  mode?: LifestyleGenerationMode;
  generation: LifestyleGenerationItem;
  isGenerating: boolean;
  generateError: string | null;
  pendingEditRestore: { prompt: string; referenceFileUrls?: string[] } | null;
  onBack: () => void;
  onEditGenerate: (
    sourceItem: LifestyleGenerationItem,
    editPrompt: string,
    editReferenceFileUrls?: string[]
  ) => void;
  onClearPendingEditRestore: () => void;
}

function ModelViewerLifestyleImageOverlay({
  url,
  modelName,
  mode,
  generation,
  isGenerating,
  generateError,
  pendingEditRestore,
  onBack,
  onEditGenerate,
  onClearPendingEditRestore
}: ModelViewerLifestyleImageOverlayProps) {
  const { t } = useTranslate();
  const [isEditModeActive, setIsEditModeActive] = useState(false);
  const [editPrompt, setEditPrompt] = useState('');

  // Edit references live in the overlay, separate from the model-view prompt
  // card list, so attachments never leak between the two flows.
  const {
    referenceFiles,
    referenceFileUrls,
    isUploadingReference,
    applyReferenceFiles,
    handleReferenceFilesChange,
    handleRemoveReference,
    restoreReferences
  } = useReferenceFilesUpload();

  useEffect(() => {
    if (pendingEditRestore) {
      setEditPrompt(pendingEditRestore.prompt);
      restoreReferences(pendingEditRestore.referenceFileUrls ?? []);
      setIsEditModeActive(true);
      onClearPendingEditRestore();
    } else {
      setIsEditModeActive(false);
      setEditPrompt('');
      restoreReferences([]);
    }
  }, [generation.localId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = useCallback(() => {
    downloadFile({
      downloadFile: {
        file: url,
        name: buildDownloadName(modelName, mode, url)
      },
      withBlob: true
    });
  }, [url, modelName, mode]);

  const handleEditGenerate = useCallback(() => {
    onEditGenerate(generation, editPrompt, referenceFileUrls);
    setIsEditModeActive(false);
    setEditPrompt('');
    restoreReferences([]);
  }, [
    generation,
    editPrompt,
    referenceFileUrls,
    onEditGenerate,
    restoreReferences
  ]);

  const handleReferenceFileDrop = useCallback(
    (file: File) => applyReferenceFiles([file]),
    [applyReferenceFiles]
  );

  return (
    <div className="absolute inset-0 z-30 bg-white dark:bg-gray-900 flex items-center justify-center">
      <StaticImageHelper
        src={url}
        alt={t(modelViewersKeys.generatedLifestyle)}
        className="w-full h-full object-contain"
      />

      <CheckPermissions
        action={ModelViewerPermissions.READ_MODEL_VIEWER_LIFESTYLE_GENERATOR}
      >
        {!isEditModeActive && (
          <div className="absolute bottom-4 right-4 z-40">
            <PureIconButtonHelper
              icon={IconsEnum.SPARKLES_SOLID}
              iconClassName="h-4 w-4"
              i18nText={modelViewersKeys.aiImageryTool}
              i18nTextClassName="ml-1.5"
              className="py-3 pl-3 pr-6 space-x-1 rounded-full inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base text-white bg-white/15 dark:bg-black/70 hover:bg-white/5 dark:hover:bg-black/25 border-cosmos animate-gradient-angle shadow-sm hover:shadow-md transition-colors duration-150 ease-out"
              onClick={() => setIsEditModeActive(true)}
            />
          </div>
        )}

        {isEditModeActive && (
          <ModelViewerImageEditPromptCard
            prompt={editPrompt}
            isGenerating={isGenerating}
            generateError={generateError}
            references={referenceFiles}
            isUploadingReference={isUploadingReference}
            onPromptChange={setEditPrompt}
            onReferenceFileChange={handleReferenceFilesChange}
            onReferenceFileDrop={handleReferenceFileDrop}
            onRemoveReference={handleRemoveReference}
            onGenerate={handleEditGenerate}
            onMinimize={() => setIsEditModeActive(false)}
          />
        )}
      </CheckPermissions>

      <div className="absolute bottom-4 left-4 flex items-center gap-2 z-40">
        <PureIconButtonHelper
          icon={IconsEnum.CHEVRON_LEFT}
          iconClassName="h-5 w-5"
          i18nText={modelViewersKeys.backToModel}
          className="py-1.5 pl-1.5 pr-3 space-x-1 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:text-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
          onClick={onBack}
        />
        <PureIconButtonHelper
          icon={IconsEnum.DOWNLOAD_SOLID}
          iconClassName="h-5 w-5"
          i18nText={words.download}
          className="py-1.5 pl-1.5 pr-3 space-x-1 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:text-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
          onClick={handleDownload}
        />
      </div>
    </div>
  );
}

export default ModelViewerLifestyleImageOverlay;
